import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import { hasPermission as checkPermission } from '../utils/permissions';

// Create the Auth Context
const AuthContext = createContext(null);

// AuthProvider component that wraps the app
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('auth_token'));
    const [onboarding, setOnboardingState] = useState(() => {
        const raw = localStorage.getItem('onboarding_state');
        if (!raw) {
            return { pending: false, stage: 'done', path: '/dashboard' };
        }

        try {
            return JSON.parse(raw);
        } catch (error) {
            return { pending: false, stage: 'done', path: '/dashboard' };
        }
    });

    const normalizeUserPayload = useCallback((payload) => {
        if (!payload) return null;
        if (payload.user) {
            return payload.user;
        }
        return payload;
    }, []);

    const persistOnboarding = useCallback((nextOnboarding) => {
        const normalized = nextOnboarding || { pending: false, stage: 'done', path: '/dashboard' };
        localStorage.setItem('onboarding_state', JSON.stringify(normalized));
        setOnboardingState(normalized);
    }, []);

    const bootstrapAuth = (accessToken, userPayload) => {
        localStorage.setItem('auth_token', accessToken);
        setToken(accessToken);

        const normalizedUser = normalizeUserPayload(userPayload);
        setUser(normalizedUser);
        persistOnboarding(normalizedUser?.onboarding);
    };

    // Check if user is authenticated
    const checkAuth = useCallback(async () => {
        const storedToken = localStorage.getItem('auth_token');
        
        if (storedToken) {
            try {
                // Verify token and get user data
                const response = await api.get('/user');
                const normalizedUser = normalizeUserPayload(response.data);
                setUser(normalizedUser);
                persistOnboarding(response.data?.onboarding || normalizedUser?.onboarding);
                setToken(storedToken);
            } catch (error) {
                console.error('Auth check failed:', error);
                // Token is invalid, clear it
                localStorage.removeItem('auth_token');
                localStorage.removeItem('onboarding_state');
                setToken(null);
                setUser(null);
                setOnboardingState({ pending: false, stage: 'done', path: '/dashboard' });
            }
        }
        
        setLoading(false);
    }, [normalizeUserPayload, persistOnboarding]);

    // Check authentication status on mount
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Login function
    const login = async (email, password) => {
        try {
            const response = await api.post('/login', { email, password });
            const { access_token, user: userData } = response.data;
            
            // Store token in localStorage
            localStorage.setItem('auth_token', access_token);
            setToken(access_token);
            const normalizedUser = normalizeUserPayload(userData);
            setUser(normalizedUser);
            persistOnboarding(normalizedUser?.onboarding);
            
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    // Logout function
    const logout = async () => {
        try {
            // Call logout API
            await api.post('/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear local state and storage regardless of API call result
            localStorage.removeItem('auth_token');
            localStorage.removeItem('onboarding_state');
            setToken(null);
            setUser(null);
            setOnboardingState({ pending: false, stage: 'done', path: '/dashboard' });
        }
    };

    // Register function
    const register = async (userData) => {
        try {
            const response = await api.post('/register', userData);
            const { access_token, user: newUser } = response.data;
            
            // Store token in localStorage
            localStorage.setItem('auth_token', access_token);
            setToken(access_token);
            const normalizedUser = normalizeUserPayload(newUser);
            setUser(normalizedUser);
            persistOnboarding(normalizedUser?.onboarding);
            
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    // Update user data
    const updateUser = (updatedUserData) => {
        setUser(prevUser => ({
            ...prevUser,
            ...updatedUserData
        }));
        if (updatedUserData?.onboarding) {
            persistOnboarding(updatedUserData.onboarding);
        }
    };

    // Refresh user data from API
    const refreshUser = async () => {
        try {
            const response = await api.get('/user');
            const normalized = normalizeUserPayload(response.data);
            setUser(normalized);
            persistOnboarding(response.data?.onboarding || normalized?.onboarding);
            return normalized;
        } catch (error) {
            console.error('Failed to refresh user:', error);
            throw error;
        }
    };

    const setOnboarding = (nextOnboarding) => {
        persistOnboarding(nextOnboarding);
    };

    // Set token manually (useful for OAuth callbacks)
    const setAuthToken = async (newToken) => {
        localStorage.setItem('auth_token', newToken);
        setToken(newToken);
        await checkAuth(); // Fetch user data with new token
    };

    // Context value
    const value = {
        user,
        token,
        hasPermission: (permission) => checkPermission(user, permission),
        login,
        logout,
        register,
        updateUser,
        refreshUser,
        bootstrapAuth,
        setAuthToken,
        onboarding,
        onboardingPending: !!onboarding?.pending,
        onboardingStage: onboarding?.stage || 'done',
        onboardingPath: onboarding?.path || '/dashboard',
        setOnboarding,
        loading,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the Auth Context
export const useAuth = () => {
    const context = useContext(AuthContext);
    
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    
    return context;
};

export default AuthContext;
